import React from 'react';
import { Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload';
import { MediaUploadConfig, handleFileUpload } from '../../utils/file.util';
import { useTranslation } from 'react-i18next';

export interface FileUploadFieldProps {
  value?: RcFile | RcFile[] | null;
  onChange?: (files: RcFile | RcFile[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  errorMsg?: string;
  placeholder?: string;
  uploadConfig: MediaUploadConfig;
  onError?: (error: string) => void;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  value,
  onChange,
  multiple = false,
  disabled = false,
  className,
  errorMsg,
  placeholder = undefined,
  uploadConfig,
  onError,
}) => {
  const { t } = useTranslation();

  placeholder =
    placeholder || (multiple ? t('crud.create.uploadFiles') : t('crud.create.uploadFile'));

  const handleChange = (info: { fileList: UploadFile[] }) => {
    const result = handleFileUpload(
      info.fileList,
      value ? (Array.isArray(value) ? value : [value]) : null,
      {
        ...uploadConfig,
        multiple,
      }
    );

    if (result.error) {
      if (onError) {
        onError(result.error);
      } else {
        message.error(result.error);
      }
      return;
    }

    if (onChange) {
      if (multiple) {
        onChange(result.files.length > 0 ? result.files : null);
      } else {
        onChange(result.files[0] || null);
      }
    }
  };

  const fileList = React.useMemo(() => {
    if (!value) return [];

    const files = Array.isArray(value) ? value : [value];
    return files.map((file, index) => ({
      uid: file.uid || `-${index + 1}`,
      name: file.name,
      status: 'done' as const,
      url: URL.createObjectURL(file),
      originFileObj: file,
    }));
  }, [value]);

  return (
    <div className={className}>
      <Upload.Dragger
        multiple={multiple}
        disabled={disabled}
        beforeUpload={() => false}
        fileList={fileList}
        onChange={handleChange}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{placeholder}</p>
      </Upload.Dragger>
      {errorMsg && <div className="ant-form-item-explain-error">{errorMsg}</div>}
    </div>
  );
};

export default FileUploadField;
